import apiClient from "../../client/apiClient"
import { appendQueryParams } from "../../utils"

export type Permission = {
  _id: string
  module: string
  actions: string[]
  role: string
  createdAt: string
  updatedAt: string
}

export type Role = {
  _id: string
  name: string
  permissions: Permission[]
  createdBy?: {
    _id: string
    name: string
    email: string
  }
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export type ModulePermission = {
  module: string
  actions: string[]
}

export type CreateRoleData = {
  name: string
  modules: ModulePermission[]
}

export type UpdateRoleData = {
  name?: string
  modules?: ModulePermission[]
  // Campus these grants apply to. Permissions are per (role, module, branch).
  branch?: string
}

export type PaginationData = {
  currentPage: number
  hasNextPage: boolean
  hasPrevPage: boolean
  limit: number
  total: number
  totalPages: number
  nextPage: number | null
  prevPage: number | null
}

export type RolesApiResponse = {
  data: Role[]
  pagination: PaginationData
}

export type RoleApiResponse = {
  data: Role
}

export type BaseApiResponse = {
  success: boolean
  message: string
}

export type RoleFilters = {
  page?: number
  limit?: number
  search?: string
  sortBy?: string
  order?: "asc" | "desc"
  context?: string
}

export type Module = {
  _id: string
  name: string
  actions: string[]
  isActive: boolean
  createdAt: string
  updatedAt: string
  createdBy: string
}

const ROLE_ENDPOINT = {
  ROLES: "/role",
  MODULES: "/module",
}

// Create a new role
const createRole = async (
  roleData: CreateRoleData
): Promise<RoleApiResponse> => {
  const response = await apiClient({
    url: `${ROLE_ENDPOINT.ROLES}/create`,
    method: "POST",
    data: roleData,
  })
  return response.data
}

// Get all roles with pagination and filters
const getAllRoles = async ({
  page = 1,
  limit = 10,
  search,
  sortBy = "createdAt",
  order = "desc",
  context,
}: RoleFilters = {}): Promise<RolesApiResponse> => {
  const response = await apiClient({
    url: appendQueryParams(ROLE_ENDPOINT.ROLES, {
      page,
      limit,
      search,
      sortBy,
      order,
      context,
    }),
    method: "GET",
  })
  return response.data
}

// Get role by ID. Permissions are per-campus — pass `branch` to load that
// campus's grants (omit to use the currently-selected campus server-side).
const getRoleById = async (id: string, branch?: string): Promise<RoleApiResponse> => {
  const response = await apiClient({
    url: appendQueryParams(`${ROLE_ENDPOINT.ROLES}/${id}`, branch ? { branch } : {}),
    method: "GET",
  })
  return response.data
}

// Update role
const updateRole = async (
  id: string,
  roleData: UpdateRoleData
): Promise<RoleApiResponse> => {
  const response = await apiClient({
    url: `${ROLE_ENDPOINT.ROLES}/${id}`,
    method: "PUT",
    data: roleData,
  })
  return response.data
}

// Soft delete role
const deleteRole = async (id: string): Promise<BaseApiResponse> => {
  const response = await apiClient({
    url: `${ROLE_ENDPOINT.ROLES}/${id}`,
    method: "DELETE",
  })
  return response.data
}

// Get modules with permissions from backend
export const getModulesWithPermissions = async ({
  page = 1,
  limit = 10,
}: {
  page?: number
  limit?: number
} = {}): Promise<{ modules: Module[]; pagination: any }> => {
  try {
    const response = await apiClient({
      url: ROLE_ENDPOINT.MODULES,
      method: "GET",
      params: {
        page,
        limit,
      },
    })

    if (response.data.success) {
      return {
        modules: response.data.data || [],
        pagination: response.data.pagination,
      }
    }
    return { modules: [], pagination: {} }
  } catch (error) {
    console.error("Error fetching modules:", error)
    return { modules: [], pagination: {} }
  }
}

export { createRole, getAllRoles, getRoleById, updateRole, deleteRole }
